/**
 * Centrale configuratie. Alles komt uit environment-variabelen (.env),
 * met veilige defaults zodat de app zonder configuratie op mockdata draait.
 */

export type DataProvider = 'mock' | 'supabase';
export type BagProvider = 'mock' | 'pdok';

export interface AppConfig {
  dataProvider: DataProvider;
  bagProvider: BagProvider;
  supabase: {
    url: string;
    /** Publieke sleutel. Hoort in de frontend te staan; de beveiliging
     *  zit in de row level security-regels van de database, niet in de sleutel. */
    publicKey: string;
    fotoBucket: string;
  };
  map: {
    center: [number, number];
    zoom: number;
    minZoom: number;
    maxZoom: number;
    /** Onder dit zoomniveau worden geen BAG-objecten opgehaald. */
    bagMinZoom: number;
  };
  /** Concept elke X ms wegschrijven naar localStorage. */
  autosaveIntervalMs: number;
}

const env = import.meta.env;

function num(value: string | undefined, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

let config: AppConfig = {
  dataProvider: (env.VITE_DATA_PROVIDER as DataProvider) || 'mock',
  bagProvider: (env.VITE_BAG_PROVIDER as BagProvider) || 'mock',
  supabase: {
    url: env.VITE_SUPABASE_URL || '',
    publicKey: env.VITE_SUPABASE_PUBLIC_KEY || '',
    fotoBucket: env.VITE_SUPABASE_FOTO_BUCKET || 'afkoppelfotos',
  },
  map: {
    // Standaard: Geertruidenberg centrum.
    center: [num(env.VITE_MAP_CENTER_LAT, 51.7005), num(env.VITE_MAP_CENTER_LON, 4.857)],
    zoom: num(env.VITE_MAP_ZOOM, 16),
    minZoom: 11,
    maxZoom: 20,
    bagMinZoom: 16,
  },
  autosaveIntervalMs: 1500,
};

export function getConfig(): AppConfig {
  return config;
}

/** Overschrijvingen mogen ook maar een deel van de geneste objecten zetten. */
export interface AppConfigOverrides
  extends Partial<Omit<AppConfig, 'supabase' | 'map'>> {
  supabase?: Partial<AppConfig['supabase']>;
  map?: Partial<AppConfig['map']>;
}

export function configureApp(overrides: AppConfigOverrides): void {
  config = {
    ...config,
    ...overrides,
    supabase: { ...config.supabase, ...overrides.supabase },
    map: { ...config.map, ...overrides.map },
  };
}

/* ------------------------------------------------------------------ */
/* Kaartlagen                                                          */
/* ------------------------------------------------------------------ */

export interface BasemapLaag {
  id: string;
  naam: string;
  url: string;
  attribution: string;
  maxNativeZoom: number;
}

/**
 * PDOK levert de landsdekkende luchtfoto als RESTful WMTS in EPSG:3857,
 * direct bruikbaar als Leaflet-tilelayer. Voor afkoppelen is de luchtfoto
 * bruikbaarder dan een topografische kaart: je ziet daken en verharding.
 */
export const BASEMAPS: BasemapLaag[] = [
  {
    id: 'luchtfoto',
    naam: 'Luchtfoto',
    url: 'https://service.pdok.nl/hwh/luchtfotorgb/wmts/v1_0/Actueel_orthoHR/EPSG:3857/{z}/{x}/{y}.jpeg',
    attribution: 'Luchtfoto &copy; PDOK / Beeldmateriaal.nl',
    maxNativeZoom: 19,
  },
  {
    id: 'topografie',
    naam: 'Topografie',
    url: 'https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/standaard/EPSG:3857/{z}/{x}/{y}.png',
    attribution: 'Kaart &copy; PDOK / Kadaster BRT-Achtergrondkaart',
    maxNativeZoom: 19,
  },
];
